import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Card from "../card";
import StackList from "../stackList";
import { SchoolFeeStudentResult } from "../../models/schoolFee";

// Display order is Academic period -> Course -> Level, ALWAYS -- in the result
// list, the summary and the confirmation. It is stated normatively upstream
// (CORE-Service-Listing-API-v1.6.0-final.yaml:101) and it is the order a
// parent reads a placement in, whatever the labels happen to say.
const placementRows = (
  enrolment: SchoolFeeStudentResult["enrolments"][number],
  labels: { period: string; course: string; level: string }
) => [
  { title: labels.period, value: enrolment.period.label },
  { title: labels.course, value: enrolment.course.name },
  { title: labels.level, value: enrolment.level.name },
];

const fullName = (student: SchoolFeeStudentResult["student"]) =>
  `${student.first_name} ${student.last_name}`.trim();

/**
 * The students a search returned. One hit is still a list: selecting is what
 * carries the identity into the journey, so a single result is confirmed by
 * the payer rather than assumed for them.
 */
const StudentResultList = ({
  results,
  onSelect,
}: {
  results: SchoolFeeStudentResult[];
  onSelect: (hit: SchoolFeeStudentResult) => void;
}) => {
  const { t } = useTranslation();

  const labels = {
    period: t("schoolfee.student.period"),
    course: t("schoolfee.student.course"),
    level: t("schoolfee.student.level"),
  };

  return (
    <Box mt={3}>
      {results.length > 1 && (
        <Box mb={2}>
          <Typography variant="h6" fontWeight="bold">
            {t("schoolfee.search.multiple_title")}
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            {t("schoolfee.search.multiple_body")}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {results.map(hit => (
          <Card key={hit.student.student_uuid}>
            <Box sx={{ p: 2 }}>
              <Typography variant="body1" fontWeight="700">
                {fullName(hit.student)}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                {t("schoolfee.search.matricule_label")}: {hit.student.matricule}
              </Typography>

              {hit.enrolments.map(enrolment => (
                <Box key={enrolment.period.period_uuid} mt={2}>
                  <StackList
                    spacing={1}
                    margin={0}
                    list={placementRows(enrolment, labels)}
                  />
                </Box>
              ))}

              <Box mt={2}>
                <Button
                  variant="contained"
                  onClick={() => onSelect(hit)}
                  sx={{ textTransform: "none" }}
                >
                  {t("schoolfee.search.select", {
                    name: fullName(hit.student),
                  })}
                </Button>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default StudentResultList;
